import { onMounted, onUnmounted } from "vue";

// Keep a long-lived TV/PWA session from getting stuck on an old build.
//
// The app runs as a persistent WebView (Android TV / installed PWA) that may
// stay open for days and rarely does a full page load, so a deploy's new
// hashed bundles never reach the running instance — the user keeps seeing old
// code even though the server is up to date. This composable polls the server's
// index.html, notices when its main bundle hash changes (a deploy happened),
// and reloads at the next NON-player navigation so we never yank the screen out
// mid-playback.
//
// Mechanics: index.html is served network-first by the service worker, so a
// `no-store` fetch always returns the freshly deployed HTML with the current
// `assets/index-<hash>.js` reference. We compare that against the bundle this
// instance actually loaded.

const POLL_MS = 5 * 60 * 1000; // re-check every 5 minutes
const BUNDLE_RE = /assets\/index-[A-Za-z0-9_-]+\.js/;

export function useAppUpdate(router) {
  // Dev serves unhashed modules + HMR; the check is meaningless and could
  // false-trigger, so it's production-only.
  if (!import.meta.env.PROD) return;

  let currentBundle = null;
  let updateReady = false;
  let timer = null;

  function loadedBundle() {
    const src = [...document.scripts]
      .map((s) => s.src || "")
      .find((u) => BUNDLE_RE.test(u));
    const m = src && src.match(BUNDLE_RE);
    return m ? m[0] : null;
  }

  async function checkForUpdate() {
    // Hidden tab → skip; onVisibility() below re-checks the moment the tab
    // becomes visible again, so nothing is missed.
    if (document.visibilityState === "hidden") return;
    if (updateReady) return;
    try {
      const res = await fetch("/", {
        cache: "no-store",
        credentials: "same-origin",
      });
      if (!res.ok) return;
      const html = await res.text();
      const m = html.match(BUNDLE_RE);
      const latest = m ? m[0] : null;
      if (latest && currentBundle && latest !== currentBundle) {
        updateReady = true;
      }
    } catch {
      // Offline / transient — leave it for the next tick.
    }
  }

  function onVisibility() {
    if (document.visibilityState === "visible") checkForUpdate();
  }

  onMounted(() => {
    currentBundle = loadedBundle();
    if (!currentBundle) return; // can't identify our bundle — bail safely

    timer = setInterval(checkForUpdate, POLL_MS);
    document.addEventListener("visibilitychange", onVisibility);

    // Reload at a natural break: the next navigation that ISN'T into a player
    // (meta.cinematic). afterEach fires after the route settles, so leaving a
    // player to browse triggers the reload on the destination — never
    // interrupting playback, and never reloading straight onto a fresh player.
    router.afterEach((to) => {
      if (updateReady && !to.meta?.cinematic) {
        updateReady = false;
        window.location.reload();
      }
    });

    // First check shortly after launch (covers "deployed while the TV was
    // already sitting on the home screen").
    checkForUpdate();
  });

  onUnmounted(() => {
    if (timer) clearInterval(timer);
    document.removeEventListener("visibilitychange", onVisibility);
  });
}
